import React from 'react';
import { ResponsiveTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * Review — everything currently waiting for an HR decision.
 * (Round: HR UI Simplification — Round 2, user feedback.)
 *
 * Views (existing pages, lazy, one at a time):
 *   queue    → HrReviewDashboard      (the one pending queue)
 *   profiles → ProfileUpdateApprovals (side-by-side diff decisions)
 *   latein   → the ONE late-in & early-out place (feedback: "late-in in
 *              two places — put it wherever the logic is best"). Both
 *              existing tools now live here behind one small switch:
 *                Daily review   → LateInReview  (today's records + the
 *                                 email-notice workflow)
 *                Summary report → LateInReport  (year/month/week rollups
 *                                 by employee / department / supervisor,
 *                                 with export)
 *              Same endpoints, same "one definition of late" (the report
 *              rolls up exactly what the review screen shows). The item
 *              was REMOVED from the Reports side list; its old URLs
 *              (?section=reports&report=latein and /hr/late-in-report)
 *              land here via shell normalization / route redirect.
 *
 * The pending TOTAL still appears only inside the queue page itself.
 * The classic FTO/COA workbench keeps its single visible link here.
 */
const LazyQueue         = React.lazy(() => import('../HrReviewDashboard'));
const LazyProfiles      = React.lazy(() => import('../ProfileUpdateApprovals'));
const LazyLateIn        = React.lazy(() => import('../LateInReview'));
const LazyLateInSummary = React.lazy(() => import('../LateInReport'));

const VIEWS = [
    { key: 'queue',    label: 'Review queue',        icon: 'ri-inbox-unarchive-line' },
    { key: 'profiles', label: 'Profile approvals',   icon: 'ri-user-follow-line' },
    { key: 'latein',   label: 'Late-in & early-out', icon: 'ri-alarm-warning-line' },
];

const HrReviewSection = ({ view, lateinSub, onViewChange, onLateinSubChange }) => {
    const active = VIEWS.some((v) => v.key === view) ? view : 'queue';
    const sub = lateinSub === 'summary' ? 'summary' : 'review';

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Review</h5>
                    <span className="text-muted">Everything waiting on an HR decision.</span>
                </div>
            </div>

            <ResponsiveTabs
                tabs={VIEWS}
                activeKey={active}
                onChange={onViewChange}
                variant="pills"
                size="sm"
            />

            {active === 'queue'    && <HrEmbeddedPage><LazyQueue /></HrEmbeddedPage>}
            {active === 'profiles' && <HrEmbeddedPage><LazyProfiles /></HrEmbeddedPage>}

            {active === 'latein' && (
                <div>
                    <div
                        className="btn-group btn-group-sm mt-3"
                        role="group"
                        aria-label="Late-in view"
                    >
                        <button
                            type="button"
                            className={`btn ${sub === 'review' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => onLateinSubChange('review')}
                        >
                            Daily review
                        </button>
                        <button
                            type="button"
                            className={`btn ${sub === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => onLateinSubChange('summary')}
                        >
                            Summary report
                        </button>
                    </div>
                    <HrEmbeddedPage key={sub}>
                        {sub === 'review' ? <LazyLateIn /> : <LazyLateInSummary />}
                    </HrEmbeddedPage>
                </div>
            )}
        </div>
    );
};

export default HrReviewSection;
